import { GrowthBook } from '@growthbook/growthbook-react'

import {
  type Environment,
  GROWTHBOOK_API_HOST,
  GROWTHBOOK_CLIENT_KEY,
} from '../../config'

const clients = new Map<Environment, GrowthBook>()
const loaders = new Map<Environment, Promise<void>>()

const VISITOR_ID_KEY = 'susu-booking-tool-visitor-id'
let inMemoryVisitorId: string | undefined

/**
 * A random id, stable for as long as the tab's session lasts, sent to
 * GrowthBook as the `id` attribute so a percentage-split rule (the Select
 * Sales Rep skip pilot, RP-6031) can actually randomize per visitor.
 *
 * `sessionStorage` rather than a persisted cookie: the rules that need this
 * only have to split visitors within one booking flow, not identify them
 * across visits, and a booking widget has no business writing a long-lived
 * tracking identifier of its own — that call belongs to whoever owns consent
 * for the page it's embedded in, not to this package.
 */
export function getVisitorId(): string {
  if (inMemoryVisitorId) return inMemoryVisitorId

  if (typeof window !== 'undefined') {
    try {
      const existing = window.sessionStorage.getItem(VISITOR_ID_KEY)
      if (existing) {
        inMemoryVisitorId = existing
        return existing
      }
    } catch {
      // sessionStorage inaccessible (private mode, blocked) — fall through to
      // an id that only holds for this module's lifetime.
    }
  }

  const generated =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`

  inMemoryVisitorId = generated

  try {
    window.sessionStorage.setItem(VISITOR_ID_KEY, generated)
  } catch {
    // Same fallback as above — the id still holds for this module's lifetime.
  }

  return generated
}

/**
 * The GrowthBook client for an environment — one per page, not one per mount.
 *
 * Per-mount clients were the obvious shape and are the wrong one here:
 * `destroy()` in an unmount cleanup cannot be undone — a destroyed client stays
 * destroyed, which both StrictMode's dev remount and a host that reopens its
 * panel need it not to be — and without a cleanup each open would leak a
 * client. Sharing one also means the payload is already in memory on the second
 * open, so a screen gated on a flag resolves without a loader.
 *
 * No `autoAttributesPlugin`: it also persists a `gbuuid` cookie and collects
 * device/UTM/page-title attributes unconditionally, none of which any rule
 * here targets, and writing a cookie outside this package's own consent
 * gating is not this client's call to make. `getVisitorId` covers the one
 * thing a percentage-split rule actually needs — a stable per-visitor hash
 * attribute — without the rest.
 */
export function getGrowthBook(env: Environment): GrowthBook {
  const existing = clients.get(env)
  if (existing) return existing

  const client = new GrowthBook({
    apiHost: GROWTHBOOK_API_HOST,
    clientKey: GROWTHBOOK_CLIENT_KEY[env],
    // Exposes `window._growthbook` for the GrowthBook devtools extension,
    // which is how you force this flag locally.
    enableDevMode: env !== 'prd',
  })

  clients.set(env, client)
  return client
}

/**
 * Loads the feature payload, once per environment.
 *
 * Resolves when the load settles rather than when it succeeds, and never
 * rejects: a blocked CDN, an ad blocker or an offline dev machine has to leave
 * every flag on its default instead of parking a gated screen on its loader.
 *
 * Not streaming — one fetch per page. A flag flipped mid-session lands on the
 * visitor's next page load, which for a booking widget is soon enough and
 * costs no long-lived connection inside a host page.
 */
export function loadFeatures(env: Environment): Promise<void> {
  const pending = loaders.get(env)
  if (pending) return pending

  const loading = getGrowthBook(env)
    .init({ streaming: false })
    .then(() => undefined)
    .catch(() => undefined)

  loaders.set(env, loading)
  return loading
}
